/**
 * Form Schema Types for Custom Survey Builder
 * Defines the structure for dynamically creating survey forms
 */

export type FieldType = 
  | 'text'           // Text input
  | 'number'         // Numeric input
  | 'date'           // Date picker
  | 'dropdown'       // Select dropdown
  | 'radio'          // Radio button group (single choice)
  | 'checkbox'       // Checkbox group (multiple choice)
  | 'multiChoice'    // Multiple choice (radio/checkboxes) - legacy
  | 'boolean'        // Yes/No/N/A toggle
  | 'image'          // Image upload field
  | 'signature'      // Signature capture field
  | 'textarea';      // Multi-line text

export interface FieldOption {
  value: string;
  label: string;
}

export interface BaseFieldSchema {
  id: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  required: boolean;
  order: number;
  helpText?: string;
  defaultValue?: any;
  section?: string; // Optional section name to group fields together
  sectionOrder?: number; // Order within the section
}

export interface TextFieldSchema extends BaseFieldSchema {
  type: 'text';
  maxLength?: number;
  pattern?: string; // Regex pattern for validation
  enablePhotoCapture?: boolean; // Enable photo capture for this text field
  photoTrigger?: 'always' | 'never'; // When to show photo capture
  maxPhotos?: number; // Maximum photos allowed (default 10)
}

export interface NumberFieldSchema extends BaseFieldSchema {
  type: 'number';
  min?: number;
  max?: number;
  unit?: string; // e.g., "mm", "m²", "°C"
  step?: number;
  enablePhotoCapture?: boolean; // Enable photo capture for this number field
  photoTrigger?: 'always' | 'never'; // When to show photo capture
  maxPhotos?: number; // Maximum photos allowed (default 10)
}

export interface DateFieldSchema extends BaseFieldSchema {
  type: 'date';
  enablePhotoCapture?: boolean; // Enable photo capture for this date field
  photoTrigger?: 'always' | 'never'; // When to show photo capture
  maxPhotos?: number; // Maximum photos allowed (default 10)
}

export interface DropdownFieldSchema extends BaseFieldSchema {
  type: 'dropdown';
  options: FieldOption[];
  allowCustom?: boolean; // Allow user to enter custom value
  enablePhotoCapture?: boolean; // Enable photo capture for this dropdown field
  photoTrigger?: 'always' | 'never'; // When to show photo capture
  maxPhotos?: number; // Maximum photos allowed (default 10)
}

export interface MultiChoiceFieldSchema extends BaseFieldSchema {
  type: 'multiChoice';
  options: FieldOption[];
  allowMultiple: boolean; // Checkboxes if true, radio if false
  displayStyle?: 'inline' | 'grid' | 'list';
}

export interface BooleanFieldSchema extends BaseFieldSchema {
  type: 'boolean';
  displayStyle?: 'yes-no' | 'yes-no-na' | 'toggle';
  enablePhotoCapture?: boolean; // Enable photo capture for this boolean field
  photoTrigger?: 'always' | 'yes' | 'no' | 'never'; // When to show photo capture
  maxPhotos?: number; // Maximum photos allowed (default 10)
}

export interface ImageFieldSchema extends BaseFieldSchema {
  type: 'image';
  maxImages?: number;
  displayMode: 'cdn' | 'stacked' | 'gallery';
  maxPerPage?: number; // For PDF export (default 6)
  rowsPerPage?: number; // For PDF export (default 2)
}

export interface TextAreaFieldSchema extends BaseFieldSchema {
  type: 'textarea';
  rows?: number;
  maxLength?: number;
  enablePhotoCapture?: boolean; // Enable photo capture for this textarea field
  photoTrigger?: 'always' | 'never'; // When to show photo capture
  maxPhotos?: number; // Maximum photos allowed (default 10)
}

export interface RadioFieldSchema extends BaseFieldSchema {
  type: 'radio';
  options: FieldOption[];
  displayStyle?: 'inline' | 'grid' | 'list';
  enablePhotoCapture?: boolean; // Enable photo capture for this radio field
  photoTrigger?: 'always' | 'never'; // When to show photo capture
  maxPhotos?: number; // Maximum photos allowed (default 10)
}

export interface CheckboxFieldSchema extends BaseFieldSchema {
  type: 'checkbox';
  options: FieldOption[];
  displayStyle?: 'inline' | 'grid' | 'list';
  enablePhotoCapture?: boolean; // Enable photo capture for this checkbox field
  photoTrigger?: 'always' | 'never'; // When to show photo capture
  maxPhotos?: number; // Maximum photos allowed (default 10)
}

export interface SignatureFieldSchema extends BaseFieldSchema {
  type: 'signature';
  width?: number; // Canvas width (default 400)
  height?: number; // Canvas height (default 200)
}

export type FieldSchema = 
  | TextFieldSchema
  | NumberFieldSchema
  | DateFieldSchema
  | DropdownFieldSchema
  | RadioFieldSchema
  | CheckboxFieldSchema
  | MultiChoiceFieldSchema
  | BooleanFieldSchema
  | ImageFieldSchema
  | SignatureFieldSchema
  | TextAreaFieldSchema;

export interface PageSection {
  id: string;
  title: string;
  description?: string;
  fields: FieldSchema[];
  showInPDF: boolean;
  pdfOrder?: number;
}

export interface CustomPage {
  id: string; // e.g., 'custom_page_1'
  label: string; // Display name in navigation
  icon?: string; // Lucide icon name (optional)
  fields: FieldSchema[]; // Custom fields for this page
  createdAt: string;
  order?: number; // Display order in navigation
  showInPDF?: boolean; // Whether to include this page in PDF export (default: true)
}

export interface FormTemplate {
  id: string;
  name: string;
  version: string;
  createdAt: string;
  updatedAt: string;
  pages: PageSection[];
  metadata?: {
    author?: string;
    description?: string;
    category?: string;
  };
  // Hidden built-in fields: sectionId -> array of field names to hide
  // e.g., { 'initial_questionnaire': ['buildingName', 'flatFloorNumber'] }
  hiddenBuiltInFields?: Record<string, string[]>;
  // Custom fields added to built-in pages: sectionId -> array of custom fields
  // e.g., { 'initial_questionnaire': [{ id: 'custom_1', type: 'text', ... }] }
  customFieldsForBuiltInPages?: Record<string, FieldSchema[]>;
  // Field order for built-in sections: sectionId -> array of field IDs in display order
  // e.g., { 'initial_questionnaire': ['surveyDate', 'surveyorName', 'customerFullName', ...] }
  builtInFieldOrder?: Record<string, string[]>;
  // Hidden custom field sections: pageId -> array of section names to hide
  // e.g., { 'initial_questionnaire': ['Energy Measures', 'Accessibility Features'] }
  hiddenSections?: Record<string, string[]>;
  // Section order: pageId -> array of section IDs in display order (includes both built-in and custom sections)
  // e.g., { 'initial_questionnaire': ['survey_info', 'customer_details', 'Energy Measures', 'property_address', ...] }
  sectionOrder?: Record<string, string[]>;
  // Custom pages created by users
  customPages?: CustomPage[];
  // Page order: array of page IDs (both built-in and custom) in navigation display order
  // e.g., ['initial_questionnaire', 'custom_page_1', 'property_front', 'custom_page_2', ...]
  pageOrder?: string[];
}

/**
 * Form Template with actual data values
 */
export interface FormInstance {
  templateId: string;
  data: Record<string, any>; // Key: fieldId, Value: field value
  photos?: Record<string, any[]>; // Key: fieldId (for image fields)
}

/**
 * Default template for custom form fields in Retrofit Survey
 * NOTE: The built-in sections (initial_questionnaire, property_front, etc.) 
 * are handled separately and should NOT be included as pages here.
 */
export const DEFAULT_RETROFIT_TEMPLATE: FormTemplate = {
  id: 'default-retrofit-custom-fields',
  name: 'Custom Form Fields',
  version: '1.0.0',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  pages: [
    {
      id: 'custom_fields',
      title: 'Custom Fields',
      description: 'Add your own custom questions here',
      showInPDF: true,
      pdfOrder: 100, // Show after all built-in sections
      fields: [] as FieldSchema[], // Empty by default - users add fields via inline editor
    },
  ],
};
